import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient"; 
import Header from '../../components/Header';
import  {ArrowLeft} from "lucide-react";
const Setgoal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [profile, setProfile] = useState({
    weight: 0,
    target_weight: 0,
    height: 0, // Added height
  });

  const [activity, setActivity] = useState({
    water: 0,
    steps: 0,
    sleep: 0,
  });

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch weight, target_weight, and height
      const { data: profileData } = await supabase
        .from("profiles")
        .select("weight, target_weight, height")
        .eq("id", user.id)
        .single();

      const { data: activityData } = await supabase
        .from("daily_activity")
        .select("water, steps, sleep")
        .eq("user_id", user.id)
        .eq("date", today)
        .single();

      if (profileData) setProfile(profileData);
      if (activityData) setActivity(activityData);
      setLoading(false);
    };

    fetchData();
  }, []);

  // BMI Calculation: Weight (kg) / [Height (m)]^2
  const calculateBMI = () => {
    if (!profile.weight || !profile.height) return 0;
    const heightInMeters = profile.height / 100;
    return (profile.weight / (heightInMeters * heightInMeters)).toFixed(1);
  };

  const bmiValue = calculateBMI();

  // Determine BMI Category and Color
  const getBMIDetails = (bmi) => {
    if (bmi < 18.5) return { label: "Underweight", color: "#3B82F6" };
    if (bmi < 25) return { label: "Normal", color: "#22C55E" };
    if (bmi < 30) return { label: "Overweight", color: "#F59E0B" };
    return { label: "Obese", color: "#EF4444" };
  };

  const bmiDetails = getBMIDetails(bmiValue);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const goals = [
    {
      title: "Weight",
      value: profile.weight,
      target: profile.target_weight,
      unit: "kg",
      color: "#6366F1",
      onClick: () => navigate("/Weight"),
    },
    {
      title: "Workout",
      value: "0",
      target: "500",
      unit: "cal",
      color: "#EF4444",
      onClick: () => navigate("/Workout"),
    },
    {
      title: "Steps",
      value: activity.steps,
      target: 10000,
      unit: "steps",
      color: "#22C55E",
      onClick: () => navigate("/Steps"),
    },
    {
      title: "Sleep",
      value: activity.sleep,
      target: 8,
      unit: "h",
      color: "#3B82F6",
      onClick: () => navigate("/Sleep"),
    },
    {
      title: "Water",
      value: activity.water,
      target: 10,
      unit: "glasses",
      color: "#06B6D4",
      onClick: () => navigate("/Water"),
    },
  ];

  return (
    <div style={styles.page}>
      <Header />
          {/* BACK BUTTON */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-emerald-600 font-black uppercase text-[10px] tracking-[0.3em] mb-12 hover:translate-x-[-4px] transition-transform mt-5"
        >
          <ArrowLeft size={16} /> Back to Account
        </button>
      <header style={styles.header}>
        <h1 style={styles.heading}>Your Health</h1>
        <p style={styles.subHeading}>Stay on track with your daily targets</p>
      </header>

      <div style={styles.container}>
        {/* BMI Section */}
        <div style={{...styles.bmiCard, borderLeft: `8px solid ${bmiDetails.color}`}}>
          <div style={styles.bmiContent}>
            <div>
              <div style={styles.cardTitle}>Body Mass Index (BMI)</div>
              <div style={styles.bmiValue}>{bmiValue}</div>
              <div style={{...styles.bmiStatus, color: bmiDetails.color}}>{bmiDetails.label}</div>
            </div>
            <div style={styles.bmiInfo}>
              <p style={styles.bmiText}>Your BMI indicates you are in the <strong>{bmiDetails.label.toLowerCase()}</strong> range.</p>
            </div>
          </div>
        </div>

        

        {/* Goals Grid */}
        <div style={styles.grid}>
          {goals.map((g, i) => {
            const progress = Math.min(
              (Number(g.value) / Number(g.target)) * 100 || 0,
              100
            );

            return (
              <div key={i} style={styles.card} onClick={g.onClick}>
                <div style={styles.cardTop}>
                  <div
                    style={{
                      ...styles.iconContainer,
                      backgroundColor: `${g.color}20`,
                      color: g.color,
                    }}
                  >
                    {g.title[0]}
                  </div>
                  <span style={styles.chevron}>›</span>
                </div>

                <div>
                  <div style={styles.cardTitle}>{g.title}</div>
                  <div style={styles.value}>
                    {g.value}
                    <span style={styles.target}>
                      {" "} / {g.target} {g.unit}
                    </span>
                  </div>
                </div>

                <div style={styles.progressTrack}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${progress}%`,
                      backgroundColor: g.color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "#F8FAFC",
    fontFamily: "Inter, system-ui, sans-serif",
    paddingBottom: "40px"
  },
  container: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "0 20px"
  },
  header: {
    maxWidth: 1000,
    margin: "10px auto 32px",
    padding: "0 20px"
  },
  heading: {
    fontSize: 30,
    fontWeight: 700,
    margin: 0,
  },
  subHeading: {
    color: "#64748B",
    marginTop: 4,
  },
  bmiCard: {
    background: "#fff",
    borderRadius: 20,
    padding: "24px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
    marginBottom: "24px",
    display: "flex",
    flexDirection: "column",
  },
  bmiContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "20px"
  },
  bmiValue: {
    fontSize: 42,
    fontWeight: 800,
    color: "#1E293B",
    lineHeight: 1
  },
  bmiStatus: {
    fontSize: 18,
    fontWeight: 700,
    marginTop: 4,
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  bmiInfo: {
    maxWidth: "300px",
    background: "#F1F5F9",
    padding: "12px 16px",
    borderRadius: "12px"
  },
  bmiText: {
    fontSize: 14,
    color: "#475569",
    margin: 0,
    lineHeight: 1.5
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 20,
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    transition: "transform 0.2s ease",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
  },
  chevron: {
    fontSize: 22,
    color: "#CBD5E1",
  },
  cardTitle: {
    fontSize: 12,
    color: "#64748B",
    textTransform: "uppercase",
    fontWeight: 600,
    marginBottom: 4,
    letterSpacing: "0.5px"
  },
  value: {
    fontSize: 24,
    fontWeight: 700,
  },
  target: {
    fontSize: 14,
    color: "#94A3B8",
  },
  progressTrack: {
    height: 8,
    background: "#F1F5F9",
    borderRadius: 10,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 10,
  },
};

export default Setgoal;